/**
 * Authentik OIDC Authentication Service for Vibe Designer
 *
 * Handles:
 * - OIDC Authorization Code flow with PKCE
 * - Token storage in localStorage
 * - Token refresh
 * - Login/logout redirects
 *
 * Workstream A.10 (2026-04-26): Per-tenant OIDC config is injected at runtime by
 * nginx envsubst on `/config.js` (a shared image serves all 6 tenants — build-time
 * VITE_* vars cannot be per-tenant). authService.ts reads `window.__VIBE_CONFIG__`
 * first; falls back to `import.meta.env.VITE_*` for local Vite dev (`npm run dev`).
 */
import { randomUUID } from '../utils/randomUUID';

interface VibeConfig {
  authentik_url?: string;
  client_id?: string;
  app_slug?: string;
  // Whitelabel Phase B (2026-07-19): per-tenant brand on the same runtime rail.
  // *_b64 fields are base64 (injection-safe in the /config.js JS literal),
  // decoded by config/brand.ts. Colors are safe-charset hex.
  brand_display_name_b64?: string;
  brand_product_name_vibe_b64?: string;
  brand_color_primary?: string;
  brand_color_accent?: string;
  brand_favicon_url_b64?: string;
  brand_logo_url_b64?: string;
}

declare global {
  interface Window {
    __VIBE_CONFIG__?: VibeConfig;
  }
}

const RUNTIME_CONFIG: VibeConfig = window.__VIBE_CONFIG__ ?? {};

const AUTHENTIK_URL =
  RUNTIME_CONFIG.authentik_url ||
  import.meta.env.VITE_AUTHENTIK_URL ||
  'http://localhost:9182';
const CLIENT_ID =
  RUNTIME_CONFIG.client_id ||
  import.meta.env.VITE_AUTH_CLIENT_ID ||
  'vibe-designer-oidc';
const APP_SLUG =
  RUNTIME_CONFIG.app_slug ||
  import.meta.env.VITE_AUTH_APP_SLUG ||
  'vibe-designer';
const REDIRECT_URI = `${window.location.origin}/auth/callback`;
const TOKEN_KEY = 'vibe_designer_token';
const USER_KEY = 'vibe_designer_user';
const REFRESH_TOKEN_KEY = 'vibe_designer_refresh_token';

// Same-origin proxy prefix — routes through nginx to avoid CORS on fetch() calls.
// Browser redirects (authorization, logout) still use AUTHENTIK_URL directly.
const AUTHENTIK_PROXY = `${window.location.origin}/authentik-proxy`;

interface TokenResponse {
  access_token: string;
  refresh_token?: string;
  token_type: string;
  expires_in: number;
  id_token?: string;
}

interface UserInfo {
  sub: string;
  email: string;
  name: string;
  preferred_username: string;
  groups: string[];
}

// PKCE helpers
function generateCodeVerifier(): string {
  const array = new Uint8Array(32);
  crypto.getRandomValues(array);
  return btoa(String.fromCharCode(...array))
    .replace(/\+/g, '-')
    .replace(/\//g, '_')
    .replace(/=+$/, '');
}

// V1 Ship 2 (F-B, 2026-05-30): randomUUID polyfill extracted to src/utils/randomUUID.ts
// so voiceTabsStore + chatTabsStore can use the same Secure-Context-safe path.
// See src/utils/randomUUID.ts for the inlined algorithm + history.

// SHA-256 polyfill for plain-HTTP LAN access where `crypto.subtle` is undefined
// (Secure Context is required for SubtleCrypto; .internal LAN over HTTP isn't one).
// Implementation follows FIPS 180-4. Public domain — adapted from Wikipedia reference.
function sha256Polyfill(bytes: Uint8Array): Uint8Array {
  const K = new Uint32Array([
    0x428a2f98,0x71374491,0xb5c0fbcf,0xe9b5dba5,0x3956c25b,0x59f111f1,0x923f82a4,0xab1c5ed5,
    0xd807aa98,0x12835b01,0x243185be,0x550c7dc3,0x72be5d74,0x80deb1fe,0x9bdc06a7,0xc19bf174,
    0xe49b69c1,0xefbe4786,0x0fc19dc6,0x240ca1cc,0x2de92c6f,0x4a7484aa,0x5cb0a9dc,0x76f988da,
    0x983e5152,0xa831c66d,0xb00327c8,0xbf597fc7,0xc6e00bf3,0xd5a79147,0x06ca6351,0x14292967,
    0x27b70a85,0x2e1b2138,0x4d2c6dfc,0x53380d13,0x650a7354,0x766a0abb,0x81c2c92e,0x92722c85,
    0xa2bfe8a1,0xa81a664b,0xc24b8b70,0xc76c51a3,0xd192e819,0xd6990624,0xf40e3585,0x106aa070,
    0x19a4c116,0x1e376c08,0x2748774c,0x34b0bcb5,0x391c0cb3,0x4ed8aa4a,0x5b9cca4f,0x682e6ff3,
    0x748f82ee,0x78a5636f,0x84c87814,0x8cc70208,0x90befffa,0xa4506ceb,0xbef9a3f7,0xc67178f2,
  ]);
  const H = new Uint32Array([
    0x6a09e667,0xbb67ae85,0x3c6ef372,0xa54ff53a,0x510e527f,0x9b05688c,0x1f83d9ab,0x5be0cd19,
  ]);
  const len = bytes.length;
  const padLen = (len + 9 + 63) & ~63;
  const padded = new Uint8Array(padLen);
  padded.set(bytes);
  padded[len] = 0x80;
  const bitLen = BigInt(len) * 8n;
  const dv = new DataView(padded.buffer);
  dv.setBigUint64(padLen - 8, bitLen, false);

  const W = new Uint32Array(64);
  for (let chunk = 0; chunk < padLen; chunk += 64) {
    for (let i = 0; i < 16; i++) W[i] = dv.getUint32(chunk + i * 4, false);
    for (let i = 16; i < 64; i++) {
      const s0 = ((W[i-15] >>> 7) | (W[i-15] << 25)) ^ ((W[i-15] >>> 18) | (W[i-15] << 14)) ^ (W[i-15] >>> 3);
      const s1 = ((W[i-2] >>> 17) | (W[i-2] << 15)) ^ ((W[i-2] >>> 19) | (W[i-2] << 13)) ^ (W[i-2] >>> 10);
      W[i] = (W[i-16] + s0 + W[i-7] + s1) >>> 0;
    }
    let [a,b,c,d,e,f,g,h] = H;
    for (let i = 0; i < 64; i++) {
      const S1 = ((e >>> 6) | (e << 26)) ^ ((e >>> 11) | (e << 21)) ^ ((e >>> 25) | (e << 7));
      const ch = (e & f) ^ (~e & g);
      const t1 = (h + S1 + ch + K[i] + W[i]) >>> 0;
      const S0 = ((a >>> 2) | (a << 30)) ^ ((a >>> 13) | (a << 19)) ^ ((a >>> 22) | (a << 10));
      const mj = (a & b) ^ (a & c) ^ (b & c);
      const t2 = (S0 + mj) >>> 0;
      h = g; g = f; f = e; e = (d + t1) >>> 0;
      d = c; c = b; b = a; a = (t1 + t2) >>> 0;
    }
    H[0] = (H[0] + a) >>> 0; H[1] = (H[1] + b) >>> 0;
    H[2] = (H[2] + c) >>> 0; H[3] = (H[3] + d) >>> 0;
    H[4] = (H[4] + e) >>> 0; H[5] = (H[5] + f) >>> 0;
    H[6] = (H[6] + g) >>> 0; H[7] = (H[7] + h) >>> 0;
  }
  const out = new Uint8Array(32);
  const odv = new DataView(out.buffer);
  for (let i = 0; i < 8; i++) odv.setUint32(i * 4, H[i], false);
  return out;
}

async function generateCodeChallenge(verifier: string): Promise<string> {
  const encoder = new TextEncoder();
  const data = encoder.encode(verifier);
  // Prefer native SubtleCrypto when in a Secure Context (HTTPS or localhost);
  // fall back to the polyfill on plain HTTP LAN where crypto.subtle is undefined.
  // Defensive: check the method directly so a stub `subtle` object without `digest`
  // also falls through to the polyfill rather than throwing at call time.
  const digestBytes: Uint8Array =
    typeof crypto.subtle?.digest === 'function'
      ? new Uint8Array(await crypto.subtle.digest('SHA-256', data))
      : sha256Polyfill(data);
  return btoa(String.fromCharCode(...digestBytes))
    .replace(/\+/g, '-')
    .replace(/\//g, '_')
    .replace(/=+$/, '');
}

/**
 * Decode a JWT's `exp` claim (seconds since the Unix epoch) WITHOUT verifying
 * the signature — the backend is the real verifier; the SPA only needs the
 * expiry to avoid mounting the app with a dead token. Returns null when the
 * token is not a parseable 3-part JWT or carries no numeric `exp` (opaque
 * tokens: no local expiry signal → callers defer to the backend 401 path).
 */
function decodeJwtExp(token: string): number | null {
  const parts = token.split('.');
  if (parts.length !== 3) return null;
  try {
    const b64 = parts[1].replace(/-/g, '+').replace(/_/g, '/');
    const padded = b64 + '='.repeat((4 - (b64.length % 4)) % 4);
    const payload = JSON.parse(atob(padded)) as { exp?: unknown };
    return typeof payload.exp === 'number' ? payload.exp : null;
  } catch {
    return null;
  }
}

class AuthService {
  // Use same-origin proxy for discovery fetch to avoid CORS
  private discoveryUrl = `${AUTHENTIK_PROXY}/application/o/${APP_SLUG}/.well-known/openid-configuration`;
  private discoveryCache: Record<string, string> | null = null;
  // Single-flight guard so concurrent callers share one refresh request — with
  // Authentik rotation a second POST of the same single-use refresh token fails.
  private refreshInFlight: Promise<boolean> | null = null;

  /**
   * Rewrite an Authentik URL to use the same-origin proxy (for fetch calls).
   * Browser redirects should NOT use this — they navigate directly to Authentik.
   */
  private proxyUrl(url: string): string {
    return url.replace(AUTHENTIK_URL, AUTHENTIK_PROXY);
  }

  /**
   * Fetch OIDC discovery document (cached)
   */
  async getDiscovery(): Promise<Record<string, string>> {
    if (this.discoveryCache) return this.discoveryCache;
    const resp = await fetch(this.discoveryUrl);
    this.discoveryCache = await resp.json();
    return this.discoveryCache!;
  }

  /**
   * Start OIDC login flow — redirects to Authentik
   */
  async login(): Promise<void> {
    const discovery = await this.getDiscovery();
    const codeVerifier = generateCodeVerifier();
    const codeChallenge = await generateCodeChallenge(codeVerifier);

    // Store code verifier for callback
    sessionStorage.setItem('pkce_code_verifier', codeVerifier);

    const params = new URLSearchParams({
      client_id: CLIENT_ID,
      response_type: 'code',
      redirect_uri: REDIRECT_URI,
      // offline_access → Authentik issues a refresh token (silent renewal).
      scope: 'openid email profile offline_access',
      code_challenge: codeChallenge,
      code_challenge_method: 'S256',
      state: randomUUID(),
    });

    window.location.href = `${discovery.authorization_endpoint}?${params}`;
  }

  /**
   * Handle OIDC callback — exchange code for tokens
   */
  async handleCallback(code: string): Promise<boolean> {
    const discovery = await this.getDiscovery();
    const codeVerifier = sessionStorage.getItem('pkce_code_verifier');
    sessionStorage.removeItem('pkce_code_verifier');

    if (!codeVerifier) {
      console.error('[Auth] No PKCE code verifier found');
      return false;
    }

    const resp = await fetch(this.proxyUrl(discovery.token_endpoint), {
      method: 'POST',
      headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
      body: new URLSearchParams({
        grant_type: 'authorization_code',
        client_id: CLIENT_ID,
        code,
        redirect_uri: REDIRECT_URI,
        code_verifier: codeVerifier,
      }),
    });

    if (!resp.ok) {
      console.error('[Auth] Token exchange failed:', resp.status);
      return false;
    }

    const tokens: TokenResponse = await resp.json();
    localStorage.setItem(TOKEN_KEY, tokens.access_token);
    // Persist the refresh token (offline_access) for silent renewal.
    if (tokens.refresh_token) {
      localStorage.setItem(REFRESH_TOKEN_KEY, tokens.refresh_token);
    }

    // Fetch user info (via proxy to avoid CORS)
    const userResp = await fetch(this.proxyUrl(discovery.userinfo_endpoint), {
      headers: { Authorization: `Bearer ${tokens.access_token}` },
    });

    if (userResp.ok) {
      const user: UserInfo = await userResp.json();
      localStorage.setItem(USER_KEY, JSON.stringify(user));
    }

    return true;
  }

  /**
   * Logout — clear tokens and redirect to Authentik logout
   */
  async logout(): Promise<void> {
    const token = this.getToken();
    this.clearStoredAuth();

    const discovery = await this.getDiscovery();
    if (discovery.end_session_endpoint && token) {
      window.location.href = `${discovery.end_session_endpoint}?post_logout_redirect_uri=${encodeURIComponent(window.location.origin)}`;
    } else {
      window.location.href = '/';
    }
  }

  /**
   * Get stored access token
   */
  getToken(): string | null {
    return localStorage.getItem(TOKEN_KEY);
  }

  /**
   * Get the stored refresh token (offline_access), or null. Used by the silent
   * refresh scheduler + the boot/401/pi guards to decide whether renewal is
   * possible before falling back to /login.
   */
  getRefreshToken(): string | null {
    return localStorage.getItem(REFRESH_TOKEN_KEY);
  }

  /**
   * Get stored user info
   */
  getUser(): UserInfo | null {
    const raw = localStorage.getItem(USER_KEY);
    return raw ? JSON.parse(raw) : null;
  }

  /**
   * True when `token` is a JWT whose `exp` is in the past (optionally minus a
   * clock-skew guard). Opaque / unparseable / exp-less tokens return false —
   * no local expiry signal, so defer to the backend 401 path. Pure function of
   * its argument so it is unit-testable without localStorage.
   */
  isTokenExpired(token: string, skewSeconds = 0): boolean {
    const exp = decodeJwtExp(token);
    if (exp === null) return false;
    // Strict `<` per RFC 7519 §4.1.4: a token is valid UNTIL the clock passes
    // `exp` (not at exactly `exp`). `<=` would log a user out one second early.
    return exp < Math.floor(Date.now() / 1000) + skewSeconds;
  }

  /**
   * Remove the stored token + user WITHOUT the Authentik end-session redirect.
   * Used by AuthProvider when a present-but-expired token is detected at boot
   * (mirrors api.ts handle401StaleToken's local clear, minus the navigation).
   */
  clearStoredAuth(): void {
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(USER_KEY);
    localStorage.removeItem(REFRESH_TOKEN_KEY);
  }

  /**
   * Check if the user is authenticated: a token must be PRESENT and, when it
   * is a JWT, not expired. Pre-2026-06-25 this checked presence only
   * (`!!getToken()`), so an expired 8h token (no refresh) read as authenticated
   * → the app mounted and fired boot calls + the voice WS with a dead token →
   * 401 storm + the "unauthorized: voice WS requires a valid auth frame" banner.
   */
  isAuthenticated(): boolean {
    const token = this.getToken();
    return !!token && !this.isTokenExpired(token);
  }

  /**
   * Silently renew the access token using the stored refresh token. SPA-direct
   * (mirrors handleCallback's token-endpoint POST; public PKCE client, no
   * secret); Authentik rotates the refresh token on each use. SINGLE-FLIGHT:
   * concurrent callers (proactive timer + 401 retry + boot) share one request,
   * because a second POST of the same single-use refresh token would fail.
   * Returns true on success (new access + rotated refresh stored); false when
   * no refresh token exists or the renewal fails (stored auth is cleared so the
   * caller falls back to /login). 2026-06-25.
   */
  async refreshAccessToken(): Promise<boolean> {
    if (this.refreshInFlight) return this.refreshInFlight;
    this.refreshInFlight = this._doRefresh();
    try {
      return await this.refreshInFlight;
    } finally {
      this.refreshInFlight = null;
    }
  }

  private async _doRefresh(): Promise<boolean> {
    const refreshToken = this.getRefreshToken();
    if (!refreshToken) return false;
    try {
      const discovery = await this.getDiscovery();
      const resp = await fetch(this.proxyUrl(discovery.token_endpoint), {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams({
          grant_type: 'refresh_token',
          client_id: CLIENT_ID,
          refresh_token: refreshToken,
        }),
      });
      if (!resp.ok) {
        console.warn('[Auth] Token refresh failed:', resp.status);
        this.clearStoredAuth();
        return false;
      }
      const tokens: TokenResponse = await resp.json();
      localStorage.setItem(TOKEN_KEY, tokens.access_token);
      // Authentik rotates: store the new single-use refresh token.
      if (tokens.refresh_token) {
        localStorage.setItem(REFRESH_TOKEN_KEY, tokens.refresh_token);
      }
      // Intentionally NOT re-fetching userinfo here: the refresh grant returns
      // only tokens, roles are enforced backend-side, and a userinfo round-trip
      // on every proactive renewal isn't worth it. The stored user object from
      // login stands for the session.
      return true;
    } catch (e) {
      console.warn('[Auth] Token refresh error:', e);
      this.clearStoredAuth();
      return false;
    }
  }

  /**
   * Milliseconds until the access token should be PROACTIVELY refreshed:
   * (exp - leadSeconds), clamped to 0 (refresh now) when within the lead window
   * or already past. Returns null when the token has no decodable exp — the
   * caller then skips scheduling. Reuses decodeJwtExp. 2026-06-25.
   */
  nextRefreshDelayMs(token: string, leadSeconds = 120): number | null {
    const exp = decodeJwtExp(token);
    if (exp === null) return null;
    return Math.max(0, (exp - leadSeconds) * 1000 - Date.now());
  }
}

export const authService = new AuthService();
export type { UserInfo, TokenResponse };
